import { useCallback, useEffect, useState } from 'react';
import { useIsAuthenticated, useUpdatedToken } from 'lib/auth/auth.hooks';
import {
  closeJobOpportunity,
  getJob,
  getJobCloseReasons as _getJobCloseReasons,
  getJobOpportunityCloseReasons,
  getJobs,
  postJob,
  postJobPublic,
  postTradesToJob,
  postTradesToJobPublic,
  cancelJob,
} from './job-apis';
import { SubmissionError } from 'lib/utils/errors';
import type { Job, JobRequestPublic } from './job.schema';
import {
  JobRequestPublicSchema,
  JobRequestSchema,
  type JobRequest,
  type JobsResponse,
} from './job.schema';
import { useJobContext } from './job.context';

export function useJobs() {
  const isAuthenticated = useIsAuthenticated();
  const updateAndCheckToken = useUpdatedToken();

  const createJob = async (job: JobRequest | JobRequestPublic) => {
    if (!isAuthenticated) {
      const parsedJob = JobRequestPublicSchema.parse(job);

      return postJobPublic({ payload: parsedJob });
    }

    const token = await updateAndCheckToken();

    const parsedJob = JobRequestSchema.parse(job);

    return postJob({
      token,
      payload: parsedJob,
    });
  };

  const addTradesToJob = async ({
    jobId,
    trades,
    email,
  }: {
    jobId: string;
    trades: number[];
    email?: string;
  }) => {
    if (!isAuthenticated) {
      if (!email) {
        throw new SubmissionError('Email is required');
      }

      return postTradesToJobPublic({
        jobId,
        payload: { trades, email },
      });
    }

    const token = await updateAndCheckToken();

    return postTradesToJob({
      jobId,
      payload: { trades },
      token,
    });
  };

  return {
    createJob,
    addTradesToJob,
  };
}

export const useJob = () => {
  const updateAndCheckToken = useUpdatedToken();
  const ctx = useJobContext();

  const getJobCloseReasons = async (jobId: string) => {
    const token = await updateAndCheckToken();
    return _getJobCloseReasons(jobId, { token });
  };

  const closeJob = async (
    jobId: string,
    { reasonId, customReason }: { reasonId: string; customReason?: string },
  ) => {
    const token = await updateAndCheckToken();

    await cancelJob(jobId, token, { reason: { id: reasonId, customReason } });

    await ctx.refetchJob();
  };

  const closeOpportunity = async (
    jobId: string,
    opportunityId: string,
    { reasonId, customReason }: { reasonId: string; customReason?: string },
  ) => {
    const token = await updateAndCheckToken();

    await closeJobOpportunity({
      jobId,
      opportunityId,
      payload: { reason: { id: reasonId, customReason } },
      token,
    });

    await ctx.refetchJob();
  };

  const getOpportunityCloseReasons = async () => {
    const token = await updateAndCheckToken();
    return getJobOpportunityCloseReasons({ token });
  };

  return {
    ...ctx,
    job: ctx.job as Job,
    closeOpportunity,
    getOpportunityCloseReasons,
    closeJob,
    getJobCloseReasons,
  };
};

export const useGetJob = (jobId?: string, defaultJob: Job | null = null) => {
  const updateAndCheckToken = useUpdatedToken();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState<null | Job>(defaultJob);

  const fetchJob = useCallback(async () => {
    let token;

    try {
      token = await updateAndCheckToken();
    } catch (e) {
      token = null;
    }

    if (!token || !jobId) return setLoading(false);

    const res = await getJob(jobId, token);

    if (!res) return;

    setJob(res as Job);
    setLoading(false);
  }, [jobId, updateAndCheckToken]);

  useEffect(() => {
    void fetchJob();
  }, [fetchJob]);

  return { loading, job, fetchJob, setJob };
};

export const useGetJobs = (requestedPage = 1) => {
  const [loading, setLoading] = useState(true);
  const updateAndCheckToken = useUpdatedToken();
  const [{ data, page, size, total }, setJobs] = useState<JobsResponse>({
    data: [],
    page: 1,
    size: 0,
    total: 0,
  });

  useEffect(() => {
    async function fetchJobs() {
      let token;

      try {
        token = await updateAndCheckToken();
      } catch (e) {
        return;
      }

      const res = await getJobs(requestedPage, token);

      setJobs(res ?? { data: [] });
      setLoading(false);
    }

    void fetchJobs();
  }, [requestedPage, updateAndCheckToken]);

  const fetchMoreJobs = async () => {
    let token;

    try {
      token = await updateAndCheckToken();
    } catch (e) {
      return;
    }

    const res = await getJobs(page + 1, token);

    setJobs({
      data: [...data, ...res.data],
      page: res.page,
      size: res.size,
      total: res.total,
    });
  };

  return {
    loading,
    jobs: data as Job[],
    page,
    size,
    total,
    fetchMoreJobs,
  };
};
